import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '../api/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetchUser = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
      console.error("AuthContext: Failed to refetch user.", error);
    }
  };

  useEffect(() => {
    const initialUserLoad = async () => {
      setLoading(true);
      await refetchUser();
      setLoading(false);
    };
    initialUserLoad();
  }, []);

  const login = async (loginData, navigate) => {
    console.log("LOGIN DEBUG:", loginData); // Debug Log
    if (loginData.token) {
      localStorage.setItem('token', loginData.token);
    }
    // After a successful login/OTP verification, the auth cookie is set.
    // Now, refetch the full user details from the '/auth/me' endpoint.
    setLoading(true);
    await refetchUser(); // This calls GET /auth/me and updates the user state globally
    setLoading(false);

    // Use the data from the initial login response to decide the immediate navigation
    if (loginData.name === 'New User' || !loginData.name) {
      navigate('/complete-profile');
    } else {
      switch (loginData.role) {
        case 'buyer':
          navigate('/owner-dashboard');
          break;
        case 'worker':
          navigate('/worker-dashboard');
          break;
        case 'broker':
          navigate('/broker-dashboard');
          break;
        case 'admin':
          navigate('/admin');
          break;
        default:
          navigate('/');
      }
    }
  };

  const logout = async (navigate) => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      navigate('/login');
    }
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    refetchUser, // Export the refetch function
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
